import { useId, useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { AchProcessingNotice } from '@/components/AchProcessingNotice'

import {
  getPaymentMethods,
  type AvailablePaymentMethodsProps,
} from './paymentMethods'

const Container = styled.div`
  background: ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.5rem;
  padding: 1.5rem 2rem;
`

const Header = styled.div<{ $divider: boolean }>`
  align-items: flex-start;
  border-bottom: ${(o) =>
    o.$divider
      ? `0.0625rem solid ${Colors.Semantic.Neutral.medium_grey}`
      : 'none'};
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding-bottom: ${(o) => (o.$divider ? '1.5rem' : '0')};
`

const Title = styled.h5`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 0;
`

const ToggleButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  line-height: 1;
  margin: 0;
  padding: 0;
`

const IntroDisclaimer = styled.p`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  margin: 1.5rem 0 0;
`

const Section = styled.div`
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  padding-top: 1.5rem;
`

const SectionTitle = styled.div`
  ${Typography.Subtitle1}
`

const SectionBody = styled.p`
  ${Typography.Body2}
  margin-bottom: 0;
`

const MethodIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: 0.75rem;
  line-height: 0.75rem;
  margin-right: 0.5rem;
`

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0 0;
`

const CollapsedNote = styled(AchProcessingNotice)`
  margin-top: 1rem;
`

const MethodNote = styled(AchProcessingNotice)`
  margin-top: 0.5rem;
`

const Chip = styled.span`
  ${Typography.Subtitle2}
  background: ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
`

export const AvailablePaymentMethodsDefault = (
  props: AvailablePaymentMethodsProps
) => {
  const {
    renderTextMap: t,
    collapsible = false,
    defaultExpanded = false,
    children,
  } = props
  const [expanded, setExpanded] = useState(defaultExpanded)
  const titleId = useId()
  const methods = getPaymentMethods(props)

  // Without `collapsible`, the component always renders its full content.
  const isExpanded = !collapsible || expanded

  return (
    <Container>
      <Header $divider={isExpanded}>
        <Title id={titleId}>{t.title}</Title>
        {collapsible && (
          <ToggleButton
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-labelledby={titleId}
          >
            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
          </ToggleButton>
        )}
      </Header>

      <IntroDisclaimer>{t.introDisclaimer}</IntroDisclaimer>

      {isExpanded ? (
        <>
          <Section>
            <SectionTitle>{t.onlineTitle}</SectionTitle>
            <SectionBody>{t.onlineDesc}</SectionBody>
          </Section>
          {methods.map((m) => (
            <Section key={m.key}>
              <SectionTitle>
                <MethodIcon icon={m.icon} />
                {m.title}
              </SectionTitle>
              <SectionBody>{m.desc}</SectionBody>
              {m.note !== undefined ? <MethodNote>{m.note}</MethodNote> : null}
            </Section>
          ))}
          {children}
        </>
      ) : (
        <>
          <ChipList>
            {methods.map((m) => (
              <Chip key={m.key}>{m.chip}</Chip>
            ))}
          </ChipList>
          {t.collapsedNote !== undefined ? (
            <CollapsedNote>{t.collapsedNote}</CollapsedNote>
          ) : null}
        </>
      )}
    </Container>
  )
}
